<template>
  <v-navigation-drawer
    :value="isVersionsSidebarVisible"
    touchless
    fixed
    app
    right
    clipped
    width="376"
    mini-variant-width="50"
    :mini-variant="isVersionsSidebarCollapsed"
    data-testid="versions-sidebar-drawer"
    @input="updateVersionsSidebarVisibility"
  >
    <div class="d-flex flex-column fill-height overflow-hidden">
      <DividedList
        v-show="!isVersionsSidebarCollapsed"
        :id="versionsSidebarContentId"
        class="versions-sidebar-content overflow-y-auto"
        data-testid="version-list"
        :items="versionable.versions"
        :empty-message="$t('no_versions_exist')"
        :list-item="VersionListItem"
      >
        <template slot="top">
          <v-subheader class="pl-3">{{ $t('versionsidebar_section_general') }}</v-subheader>
          <div class="px-2">
            <v-btn
              v-if="isInTagesordnungMode"
              depressed
              block
              class="next-item-link mb-2 py-6 body-1"
              :to="nextTagesordungItemLink"
              :disabled="!nextTagesordnungItem.id"
            >
              <div class="d-flex flex-column flex-overflow mr-2">
                <span class="body-2">{{ $t('next_tagesordnung_item') }}</span>
                <span
                  v-if="nextTagesordnungItem.title"
                  data-testid="version-sidebar-next-tagesordnung-item-title"
                  class="text-truncate"
                  >{{ nextTagesordnungItem.title }}</span
                >
                <span v-else>{{ $t('versionsidebar_no_successor_item') }}</span>
              </div>
              <v-icon class="shrink">mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              v-else
              depressed
              block
              data-testid="version-sidebar-next-agenda-item-link"
              class="next-item-link mb-2 py-6 body-1"
              :to="nextAgendaItem.link"
              :disabled="!nextAgendaItem.link"
            >
              <div class="d-flex flex-column flex-overflow mr-2 grow align-start">
                <span class="body-2">{{ $t('next_agenda_item') }}</span>
                <span
                  v-if="nextAgendaItem.title"
                  class="text-truncate"
                  data-testid="version-sidebar-next-agenda-item-item-title"
                  >{{ nextAgendaItem.title }}</span
                >
                <span v-else>{{ $t('versionsidebar_no_successor_item') }}</span>
              </div>
              <v-icon class="shrink">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <TagField
            v-if="versionable.hasOwnProperty('tags')"
            class="px-2"
            :tags="versionable.tags"
            :options="tagOptions"
            @save="saveTags"
          />
          <v-subheader class="mt-4 pl-3">{{ $t('versionsidebar_section_versions') }}</v-subheader>
        </template>
        <template slot="bottom">
          <RelatedAgendaItems
            v-if="$config.enable_businesses"
            :business-id="businessId"
            :current-agenda-item-id="currentAgendaItemId"
            :current-document="currentDocument"
          />
        </template>
      </DividedList>
      <div
        class="d-flex flex-shrink-0 mt-auto"
        :class="{ 'justify-center': isVersionsSidebarCollapsed }"
        data-testid="versions-sidebar-footer"
      >
        <v-btn
          depressed
          rounded
          height="30px"
          min-width="30px"
          color="greydark"
          class="pa-0 my-4"
          :class="{ 'mx-4': !isVersionsSidebarCollapsed }"
          data-testid="versions-sidebar-toggle"
          :aria-label="isVersionsSidebarCollapsed ? $t('Show_versions') : $t('Hide_versions')"
          :aria-expanded="String(!isVersionsSidebarCollapsed)"
          :aria-controls="versionsSidebarContentId"
          @click.stop="toggleVersionsSidebarVisibility"
        >
          <v-fade-transition mode="out-in">
            <v-icon v-if="isVersionsSidebarCollapsed" key="left">mdi-chevron-left</v-icon>
            <v-icon v-else key="right">mdi-chevron-right</v-icon>
          </v-fade-transition>
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import joinPath from 'path.join'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import VersionListItem from '@/components/listitems/VersionListItem'
import RelatedAgendaItems from '@/components/RelatedAgendaItems'
import Profile from '@/store/models/profile'

export default {
  name: 'VersionsSidebar',
  components: { RelatedAgendaItems },
  props: {
    versionable: {
      type: Object,
      default: () => ({}),
    },
    showNextAgendaItemLink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      VersionListItem,
      nextAgendaItem: {
        link: null,
        title: this.$t('nextLinkLoading'),
      },
      nextTagesordnungItem: {
        id: null,
        title: this.$t('nextLinkLoading'),
      },
    }
  },
  computed: {
    ...mapGetters({
      getVersionsSidebar: 'application/getVersionsSidebar',
    }),
    versionsSidebar() {
      return this.getVersionsSidebar
    },
    versionsSidebarContentId() {
      return `versions-sidebar-content-${this._uid}`
    },
    isVersionsSidebarCollapsed() {
      return !this.$vuetify.breakpoint.mobile && this.versionsSidebar === false
    },
    isVersionsSidebarVisible() {
      return !this.$vuetify.breakpoint.mobile || this.versionsSidebar
    },
    nextTagesordungItemLink() {
      return {
        name: 'agendaitem',
        params: { id: this.nextTagesordnungItem.agenda_item_id },
        query: { tagesordnungitem_id: this.nextTagesordnungItem.id },
      }
    },
    user() {
      return Profile.query().first()
    },
    tagOptions() {
      return this.$store.getters['tags/getTags'](this.versionable.constructor.entity, this.user)
    },
    isInTagesordnungMode() {
      return !!this.$route.query.tagesordnungitem_id
    },
    currentAgendaItemId() {
      return this.versionable.agenda_item_id || null
    },
    businessId() {
      return this.versionable.agenda_item &&
        this.versionable.agenda_item.business &&
        this.versionable.agenda_item.business.id
        ? this.versionable.agenda_item.business.id
        : null
    },
    currentDocument() {
      if (!['attachment', 'proposal'].includes(this.versionable.constructor.entity)) {
        return null
      }
      return {
        id: this.versionable.id,
        type: this.versionable.constructor.entity,
        externalId: this.versionable.external_id,
        versionId: this.versionable.version_id,
        refreshAnnotationLayers: () => this.versionable.fetchAnnotationLayers(),
      }
    },
  },
  watch: {
    '$vuetify.breakpoint.mobile'(isMobile, wasMobile) {
      if (wasMobile && !isMobile && this.versionsSidebar !== true) {
        this.setVersionsSidebar({ versionsSidebar: true })
      }
    },
    $route: {
      handler() {
        if (this.$route.query.tagesordnungitem_id) {
          this.fetchNextTagesordnungItemLink()
        } else {
          this.fetchNextAgendaItemLink()
        }
      },
      immediate: true,
    },
  },
  created() {
    if (!this.$vuetify.breakpoint.mobile && this.versionsSidebar === null) {
      this.setVersionsSidebar({ versionsSidebar: true })
    }
  },
  methods: {
    toggleVersionsSidebarVisibility() {
      this.setVersionsSidebar({ versionsSidebar: this.isVersionsSidebarCollapsed })
    },
    updateVersionsSidebarVisibility(versionsSidebar) {
      if (this.$vuetify.breakpoint.mobile) {
        this.setVersionsSidebar({ versionsSidebar })
      }
    },
    async saveTags(tags) {
      await api.post(`/v1/${this.versionable.constructor.apiPath}/${this.versionable.id}/set_tags`, { tags })
      this.versionable.constructor.fetch(this.versionable.id)
    },
    async fetchNextTagesordnungItemLink() {
      try {
        const response = await api.get(
          createLink('tagesordnung_item/{id}/next_item', { id: this.$route.query.tagesordnungitem_id }),
        )
        this.nextTagesordnungItem = response.data
      } catch (e) {
        this.nextTagesordnungItem = { id: null }
        throw e
      }
    },
    async fetchNextAgendaItemLink() {
      if (!this.showNextAgendaItemLink) {
        this.nextAgendaItem = { link: null }
        return
      }

      try {
        const response = await api.get(createLink(joinPath(this.versionable.idPath, 'next_agenda_item')))
        this.nextAgendaItem = response.data
      } catch (e) {
        this.nextAgendaItem = { link: null }
        throw e
      }
    },
    ...mapActions({
      setVersionsSidebar: 'application/setVersionsSidebar',
    }),
  },
}
</script>

<style lang="scss" scoped>
.versions-sidebar-content {
  flex: 1 1 0;
  min-height: 0;
}

/* Prevent (inconsistent) thicker border on top of active group item. */
::v-deep .v-list__group--active:before {
  background: none !important;
}
</style>
<style lang="scss">
.next-item-link {
  max-height: none;

  > .v-btn__content {
    justify-content: space-between;
    max-width: 100%;
  }
}
</style>
